import { useFeature } from '@/lib/features/FeatureProvider';
import { useModuleVisibility } from '@/hooks/useModuleVisibility';
import { useFirmType } from '@/hooks/useFirmType';
import { useProjectCategoryOptions } from '@/hooks';
import { CORE_SECTIONS, ADDON_SECTIONS, getFirmTypeVisibility } from '@/config/sectionMetadata';
import type { AddonSection } from '@/config/sectionMetadata';

export interface VisibleSectionsResult {
  /** All visible sections in canonical order: core sections first, then visible add-ins. */
  visibleSections: string[];
  /** Add-in sections that are currently hidden (for the "+ Add section" picker in R3). */
  hiddenAddIns: string[];
  /**
   * Layer-1-through-4 resolution for every add-in WITHOUT the per-project
   * Layer-5 overrides applied. This is the "default stack" — what the section's
   * visibility would be if the project carried no per-project override for it.
   *
   * Used by the override-hygiene mutation semantics: an override should exist
   * ONLY when it differs from the default stack, so removing/adding a section
   * clears the override key whenever the default stack already agrees.
   */
  defaultStackVisibility: Record<AddonSection, boolean>;
  /**
   * True when an add-in is hidden by the Layer-1 hard gate (feature flag off).
   * A per-project override can never beat the hard gate, so the deep-link banner
   * must suppress its "Show section" button for these.
   */
  hardGateHidden: Record<AddonSection, boolean>;
}

/**
 * Resolves which sections are visible for a project by applying the gate-precedence
 * stack in this order:
 *
 *  1. Hard gate: tenant feature flag — if false, section is hidden regardless.
 *  2. Soft A: firm_type — no per-section mapping in v1 (all add-ins visible).
 *  3. Soft B: module_visibility — if explicitly false, hides the section.
 *  4. Soft C: category default_sections — overrides module_visibility when non-NULL category.
 *  5. Per-project override: section_config.overrides.<key> — true forces on, false forces off.
 *
 * NULL category behavior: layer 4 is skipped; all soft defaults resolve to visible.
 * Empty section_config behavior: no per-project overrides; visibility from layers 1-4.
 * NULL category + empty section_config: all core + all add-ins visible (legacy project default).
 *
 * External-view code path does NOT use this hook (Phase 10d follow-up).
 */
export function useVisibleSections(
  project:
    | { category?: string | null; section_config?: Record<string, unknown> | null }
    | null
    | undefined,
): VisibleSectionsResult {
  const moduleVisibility = useModuleVisibility();
  const firmType = useFirmType();
  // Hard gate: portal_management feature flag gates the portal add-in.
  const portalEnabled = useFeature('portal_management');
  const { data: categoryOptionsData } = useProjectCategoryOptions();

  const hardGates: Partial<Record<AddonSection, boolean>> = {
    portal: portalEnabled,
  };

  // Resolve the active category's default_sections list.
  // Returns null when: no category, category not found, or category has no default_sections set.
  // NULL default_sections is treated as "all add-ins enabled" (consistent with migration backfill).
  const categoryOptions = Array.isArray(categoryOptionsData) ? categoryOptionsData : [];
  const matchedCategory = categoryOptions.find(
    (cat: any) => cat.value === project?.category,
  );
  const defaultSections: string[] | null = matchedCategory?.default_sections ?? null;

  const overrides: Record<string, boolean> =
    ((project?.section_config as any)?.overrides as Record<string, boolean>) ?? {};

  // Layer 2 lookup: firm_type section defaults (empty map = no restrictions in v1).
  const firmTypeDefaults = getFirmTypeVisibility(firmType);

  // ── Pass 1: resolve the default stack (Layers 1-4 only) per add-in ──────────
  const defaultStackVisibility = {} as Record<AddonSection, boolean>;
  const hardGateHidden = {} as Record<AddonSection, boolean>;

  for (const section of ADDON_SECTIONS) {
    // Layer 1: Hard gate — cannot be overridden by any soft layer or per-project override.
    if (hardGates[section] === false) {
      defaultStackVisibility[section] = false;
      hardGateHidden[section] = true;
      continue;
    }
    hardGateHidden[section] = false;

    // Base visibility: all add-ins enabled by default.
    let visible = true;

    // Layer 2: firm_type — tenant-wide soft default (lowest precedence).
    if (firmTypeDefaults[section] === false) {
      visible = false;
    } else if (firmTypeDefaults[section] === true) {
      visible = true;
    }

    // Layer 3: module_visibility — overrides firm_type for touched sections.
    if (moduleVisibility[section] === false) {
      visible = false;
    } else if (moduleVisibility[section] === true) {
      visible = true;
    }

    // Layer 4: category default_sections — only applies when project has a non-NULL category
    // AND that category has a non-NULL default_sections list.
    if (project?.category != null && defaultSections !== null) {
      visible = defaultSections.includes(section);
    }

    defaultStackVisibility[section] = visible;
  }

  // ── Pass 2: apply Layer 5 (per-project overrides) on top of the default stack ──
  const resolvedAddIns = ADDON_SECTIONS.map((section) => {
    let visible = defaultStackVisibility[section];

    // Layer 5: per-project override — highest precedence among soft layers.
    if (overrides[section] === true) {
      visible = true;
    } else if (overrides[section] === false) {
      visible = false;
    }

    // Re-apply hard gate as final check (belt-and-suspenders; an override cannot beat it).
    if (hardGates[section] === false) {
      visible = false;
    }

    return { section, visible };
  });

  const visibleAddIns = resolvedAddIns.filter((s) => s.visible).map((s) => s.section);
  const hiddenAddIns = resolvedAddIns.filter((s) => !s.visible).map((s) => s.section);

  return {
    visibleSections: [...CORE_SECTIONS, ...visibleAddIns],
    hiddenAddIns,
    defaultStackVisibility,
    hardGateHidden,
  };
}
